/**
 * ArticleImportModal.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

/**
 * ArticleImportModal Component
 * Allows importing articles from RTF files or URLs
 */

import React, { useState, useEffect } from 'react';
import { useToast } from './ToastContainer';
import { getIcon } from '../utils/icons';
import './ArticleImportModal.css';

const ArticleImportModal = ({ onClose, onSuccess }) => {
  const { showSuccess, showError } = useToast();
  const [importMethod, setImportMethod] = useState('file'); // 'file' or 'url'
  const [url, setUrl] = useState('');
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('default');
  const [downloadImages, setDownloadImages] = useState(true);

  const FALLBACK_LAYOUTS = [
    { layout_key: 'default', name: 'Standard Layout', description: 'Clean modern sidebar layout' },
    { layout_key: 'violin', name: 'Violin Article (Classic)', description: 'Elegant serif style with drop caps' },
    { layout_key: 'editorial', name: 'Editorial (Magazine)', description: 'Magazine-style headings and blockquotes' }
  ];
  const [layoutChoices, setLayoutChoices] = useState(FALLBACK_LAYOUTS);

  useEffect(() => {
    fetch('/api/content/article-layouts')
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (Array.isArray(rows) && rows.length > 0) setLayoutChoices(rows);
      })
      .catch(() => {});
  }, []);

  const layoutKey = (row) => row.layout_key || row.id;

  const renderLayoutPicker = (htmlId = 'article-template') => (
    <div className="form-group" style={{ marginTop: '1.5rem' }}>
      <label htmlFor={htmlId}>Choose style / theme</label>
      <p style={{ fontSize: '0.85rem', opacity: 0.75, margin: '0.25rem 0 0.75rem' }}>
        Layouts are managed under Admin → Templates → Article layouts (seed defaults if the list is empty).
      </p>
      <div
        className="template-selector"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginTop: '0.5rem'
        }}
      >
        {layoutChoices.map((tmp) => {
          const key = layoutKey(tmp);
          return (
            <div
              key={key}
              className={`template-option ${selectedTemplate === key ? 'active' : ''}`}
              onClick={() => setSelectedTemplate(key)}
              style={{
                padding: '1rem',
                border: '2px solid',
                borderColor: selectedTemplate === key ? 'var(--color-primary)' : 'var(--color-border)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: selectedTemplate === key ? 'var(--color-surface-hover)' : 'transparent'
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                {selectedTemplate === key && '✓ '} {tmp.name}
              </div>
              <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>{tmp.description}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type - accept both RTF and Markdown
      const validTypes = ['.rtf', '.md', '.markdown', 'application/rtf', 'text/rtf', 'text/markdown', 'text/plain'];
      const fileExt = selectedFile.name.toLowerCase().split('.').pop();

      if (fileExt === 'rtf' || fileExt === 'md' || fileExt === 'markdown' || validTypes.includes(selectedFile.type)) {
        setFile(selectedFile);
      } else {
        showError('Please select a Markdown (.md) or RTF (.rtf) file');
        e.target.value = '';
      }
    }
  };

  const handleFileImport = async () => {
    if (!file) {
      showError('Please select a file');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('articleFile', file);
    formData.append('templateId', selectedTemplate);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/content/import/file', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(`Article imported successfully: ${result.title}`);
        onSuccess();
        onClose();
      } else {
        // Try to parse as JSON, fallback to text if it fails
        let errorMessage = 'Failed to import RTF file';
        try {
          const error = await response.json();
          console.error('Import failed:', error);
          errorMessage = error.error || error.details || errorMessage;
        } catch (parseError) {
          // Response wasn't JSON, get text
          const text = await response.text();
          console.error('Import failed (non-JSON response):', text.substring(0, 200));
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        showError(errorMessage);
      }
    } catch (error) {
      console.error('Import error:', error);
      showError(`Failed to import article: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleURLImport = async () => {
    if (!url.trim()) {
      showError('Please enter a URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch (e) {
      showError('Please enter a valid URL');
      return;
    }

    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/content/import/url', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url, templateId: selectedTemplate, downloadImages })
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(`Article imported successfully: ${result.title}`);
        onSuccess();
        onClose();
      } else {
        // Try to parse as JSON, fallback to text if it fails
        let errorMessage = 'Failed to import from URL';
        try {
          const error = await response.json();
          console.error('Import failed:', error);
          errorMessage = error.error || error.details || errorMessage;
        } catch (parseError) {
          // Response wasn't JSON, get text
          const text = await response.text();
          console.error('Import failed (non-JSON response):', text.substring(0, 200));
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        showError(errorMessage);
      }
    } catch (error) {
      console.error('Import error:', error);
      showError(`Failed to import article from URL: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = (format = 'md') => {
    // Download the template in specified format
    const link = document.createElement('a');
    link.href = `/api/content/import/template/article-template.${format}`;
    link.download = `article-template.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content article-import-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{getIcon('upload')} Import Article</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Import Method Tabs */}
          <div className="import-tabs">
            <button
              className={`tab-btn ${importMethod === 'file' ? 'active' : ''}`}
              onClick={() => setImportMethod('file')}
            >
              {getIcon('document')} Upload File
            </button>
            <button
              className={`tab-btn ${importMethod === 'url' ? 'active' : ''}`}
              onClick={() => setImportMethod('url')}
            >
              {getIcon('link')} Import from URL
            </button>
          </div>

          {/* File Upload */}
          {importMethod === 'file' && (
            <div className="import-section">
              <div className="template-download">
                <p>
                  <strong>⭐ Recommended:</strong> Use Markdown (.md) for best results!
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleDownloadTemplate('md')}
                  >
                    {getIcon('download')} MD Template
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleDownloadTemplate('rtf')}
                  >
                    {getIcon('download')} RTF Template
                  </button>
                </div>
              </div>

              <div className="file-upload-area">
                <label htmlFor="rtf-file" className="file-upload-label">
                  <div className="upload-icon">{getIcon('upload')}</div>
                  <div className="upload-text">
                    {file ? (
                      <>
                        <strong>{file.name}</strong>
                        <span>Click to change file</span>
                      </>
                    ) : (
                      <>
                        <strong>Click to upload file (.md or .rtf)</strong>
                        <span>Markdown recommended</span>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    id="rtf-file"
                    accept=".rtf,.md,.markdown,application/rtf,text/rtf,text/markdown,text/plain"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              {renderLayoutPicker('article-template-file')}

              <div className="help-text">
                <p><strong>Supported Formats:</strong></p>
                <ul>
                  <li><strong>Markdown (.md)</strong> - Best choice! Simple, reliable</li>
                  <li><strong>RTF (.rtf)</strong> - Supported but may have parsing issues</li>
                </ul>
                <p style={{ marginTop: '0.5rem' }}><strong>Metadata:</strong></p>
                <ul>
                  <li>Title, Author, Publication Date, Category, Tags, Source</li>
                </ul>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleFileImport}
                  disabled={!file || importing}
                >
                  {importing ? 'Importing...' : `${getIcon('upload')} Import Article`}
                </button>
              </div>
            </div>
          )}

          {/* URL Import */}
          {importMethod === 'url' && (
            <div className="import-section">
              <div className="url-info">
                <p>
                  <strong>Import from URL:</strong> Paste a URL to an article and we'll automatically extract the content and metadata.
                </p>
                <p className="note">
                  <strong>Note:</strong> Works best with standard article pages. The system will attempt to extract:
                  title, author, content, publication date, and images.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="article-url">Article URL</label>
                <input
                  type="url"
                  id="article-url"
                  className="form-control"
                  placeholder="https://example.com/article"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
              </div>

              {renderLayoutPicker('article-template-url')}

              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={downloadImages}
                    onChange={(e) => setDownloadImages(e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      margin: 0,
                      cursor: 'pointer'
                    }}
                  />
                  <span>
                    <strong>Download Images & Media</strong>
                    <div style={{ fontSize: '0.85rem', opacity: 0.7, fontWeight: 'normal' }}>
                      If checked, images will be downloaded to your server. If unchecked, they will link to the original source.
                    </div>
                  </span>
                </label>
              </div>

              <div className="help-text">
                <p><strong>✨ Smart Import:</strong></p>
                <ul>
                  <li><strong>Media Auto-Download:</strong> Images and MP3s will be downloaded to your local server.</li>
                  <li><strong>Asset Linking:</strong> The system will automatically check if the file already exists in your library to avoid duplicates and preserve links.</li>
                  <li><strong>Standard Sites:</strong> WordPress, Medium, and most HTML blog pages are supported.</li>
                </ul>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleURLImport}
                  disabled={!url.trim() || importing}
                >
                  {importing ? 'Importing...' : `${getIcon('download')} Import from URL`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticleImportModal;

